'use client';

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { type VideoSelection } from '../../FilmStripSlider';

/** Delay before the clip starts itself, giving the source time to buffer its first frames. */
const AUTOPLAY_DELAY_MS = 500;

/** Ignore sub-frame drift so a seek triggered by our own timeupdate doesn't fight the element. */
const SEEK_EPSILON_SECONDS = 0.01;

interface UseVideoEditPlaybackArgs {
    selection: VideoSelection;
    /** Autoplay waits for this, so playback doesn't start before there is a clip to play. */
    isReady: boolean;
}

interface UseVideoEditPlaybackReturn {
    videoRef: RefObject<HTMLVideoElement | null>;
    isPlaying: boolean;
    /** Playback position in the source video, for the elapsed readout. */
    currentTime: number;
    onPlayPause: () => void;
    /** Imperative seek used while dragging — bypasses React so scrubbing stays in step with the cursor. */
    onSeek: (time: number) => void;
    onTimeUpdate: () => void;
    onPlay: () => void;
    onEnded: () => void;
}

/**
 * Playback for the trim view: the clip loops inside the selection window instead of running the
 * whole source, and the filmstrip scrubs it directly through `onSeek`.
 */
export function useVideoEditPlayback({ selection, isReady }: UseVideoEditPlaybackArgs): UseVideoEditPlaybackReturn {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(selection.startTime);

    const { startTime, endTime } = selection;

    const onPlayPause = useCallback((): void => {
        setIsPlaying((wasPlaying) => !wasPlaying);
    }, []);

    const onSeek = useCallback((time: number): void => {
        const video = videoRef.current;
        setCurrentTime(time);
        if (!video || video.seeking) return;
        video.currentTime = time;
    }, []);

    const rewindToWindowStart = useCallback(
        (video: HTMLVideoElement): void => {
            video.currentTime = startTime;
            setCurrentTime(startTime);
        },
        [startTime],
    );

    const onTimeUpdate = useCallback((): void => {
        const video = videoRef.current;
        if (!video) return;

        // Looping at the window's end is what keeps playback inside the selection.
        if (video.currentTime >= endTime) {
            rewindToWindowStart(video);
            return;
        }

        setCurrentTime(video.currentTime);
    }, [endTime, rewindToWindowStart]);

    const onPlay = useCallback((): void => {
        const video = videoRef.current;
        if (!video) return;

        // Pressing play with the playhead outside the window would play unselected footage.
        if (video.currentTime < startTime || video.currentTime >= endTime) {
            rewindToWindowStart(video);
        }
    }, [startTime, endTime, rewindToWindowStart]);

    /**
     * When the window ends at the video's end, playback stops before any `timeupdate` tick can
     * satisfy the loop check above — `ended` is the only reliable signal there. The element is
     * already paused by then, and `isPlaying` never changed, so playback has to be restarted here.
     */
    const onEnded = useCallback((): void => {
        const video = videoRef.current;
        if (!video) return;

        rewindToWindowStart(video);

        if (isPlaying) {
            void video.play().catch(() => {
                setIsPlaying(false);
            });
        }
    }, [isPlaying, rewindToWindowStart]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !isReady) return;

        if (isPlaying) {
            void video.play().catch(() => {
                // Autoplay can be refused (e.g. power saving); leave the paused UI as-is.
                setIsPlaying(false);
            });
            return;
        }
        video.pause();
    }, [isPlaying, isReady]);

    useEffect(() => {
        if (!isReady) return;

        const timer = setTimeout(() => setIsPlaying(true), AUTOPLAY_DELAY_MS);
        return () => clearTimeout(timer);
    }, [isReady]);

    // Trimming the left edge past the playhead leaves it outside the window; pull it back in.
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const clamped = Math.min(Math.max(currentTime, startTime), endTime);
        if (Math.abs(clamped - currentTime) <= SEEK_EPSILON_SECONDS) return;

        setCurrentTime(clamped);
        if (!video.seeking) {
            video.currentTime = clamped;
        }
    }, [startTime, endTime, currentTime]);

    return {
        videoRef,
        isPlaying,
        currentTime,
        onPlayPause,
        onSeek,
        onTimeUpdate,
        onPlay,
        onEnded,
    };
}
